import { useEffect, useRef, useState } from "react";
import "./Filterbar.css";

const childFilterConfig = [
  {
    label: "Ratings",
    key: "ratings",
    type: "stars",
    values: [5, 4, 3, 2, 1, "All"],
  },
  {
    label: "Drawdown",
    key: "drawdown",
    values: ["0-5%", "5-10%", "10-20%", "20-50%", "50-100%", "All"],
  },
  {
    label: "Activity",
    key: "activity",
    values: [
      "Last 24 hours",
      "Last 3 days",
      "Last 7 days",
      "Last 14 days",
      "Last Month",
      "All",
    ],
  },
  {
    label: "Min. Funding",
    key: "minFunding",
    values: [
      "Below $100",
      "$100-250",
      "$250-500",
      "$500-1000",
      "Above 1000+",
      "All",
    ],
  },
  {
    label: "Price",
    key: "price",
    values: ["$0-50", "$50-100", "$100-200", "$200-500", "$500-1000", "All"],
  },
  {
    label: "Verification",
    key: "verification",
    values: ["Yes", "No", "All"],
  },
  {
    label: "Trial",
    key: "trial",
    values: ["Yes", "No", "All"],
  },
  {
    label: "PNL",
    key: "pnl",
    values: [
      "0 to 10%",
      "10 to 25%",
      "25 to 50%",
      "50 to 100%",
      "Above 100%",
      "All",
    ],
  },
];

export const MARKET_FILTER_OPTIONS = [
  "All",
  "Both",
  "Spot",
  "Futures",
  "Not Connected",
];

export const DEFAULT_MARKET_FILTER = "All";

export const DEFAULT_CHILD_FILTERS = Object.fromEntries(
  childFilterConfig.map((f) => [f.key, "All"])
);

function StarRow({ count }) {
  return (
    <span className="star-row">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          className={`star-icon ${
            i <= count ? "star-icon--filled" : "star-icon--empty"
          }`}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </span>
  );
}

function getRatingLabel(value) {
  if (value === "All") return "All";
  return `${value} Stars`;
}

export function FilterToggleButton({ isExpanded, onToggle }) {
  return (
    <button className="filter-toggle-btn" onClick={onToggle}>
      Filters
      <svg
        className={`filter-chevron${isExpanded ? " filter-chevron--open" : ""}`}
        viewBox="0 0 10 6"
        fill="none"
      >
        <path
          d="M1 1l4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/** Parent market scope — sits beside the Filters toggle. */
export function MarketParentFilter({ value = DEFAULT_MARKET_FILTER, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const buttonLabel = `Market: ${value}`;

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  return (
    <div className="market-parent-filter" ref={wrapperRef}>
      <button
        type="button"
        className={[
          "filter-toggle-btn",
          "market-parent-filter__btn",
          isOpen ? "market-parent-filter__btn--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={buttonLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{buttonLabel}</span>
        <svg
          className={`filter-chevron${isOpen ? " filter-chevron--open" : ""}`}
          viewBox="0 0 10 6"
          fill="none"
        >
          <path
            d="M1 1l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="filter-dropdown market-parent-filter__menu" role="listbox">
          {MARKET_FILTER_OPTIONS.map((opt) => {
            const isSelected = value === opt;
            return (
              <div
                key={opt}
                role="option"
                aria-selected={isSelected}
                className={`filter-option${
                  isSelected ? " filter-option--selected" : ""
                }`}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsOpen(false);
                  onChange?.(opt);
                }}
              >
                {isSelected && (
                  <svg
                    className="filter-check-icon"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="#2563eb"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FilterBar({
  isExpanded,
  onChange,
  onResetMarket,
  marketFilter = DEFAULT_MARKET_FILTER,
}) {
  const [filters, setFilters] = useState(DEFAULT_CHILD_FILTERS);
  const [openKey, setOpenKey] = useState(null);

  const handleSelect = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    setOpenKey(null);
    onChange?.(updated);
  };

  const handleReset = () => {
    const reset = { ...DEFAULT_CHILD_FILTERS };
    setFilters(reset);
    setOpenKey(null);
    onChange?.(reset);
    onResetMarket?.();
  };

  const hasActiveFilters =
    marketFilter !== DEFAULT_MARKET_FILTER ||
    Object.values(filters).some((v) => v !== "All");

  if (!isExpanded) return null;

  return (
    <div className="filter-body">
      <div className="filter-header">
        <span></span>
        <button
          className={`filter-reset-btn${
            hasActiveFilters ? "" : " filter-reset-btn--idle"
          }`}
          onClick={handleReset}
        >
          ✕ Reset filters
        </button>
      </div>
      <div className="filter-grid">
        {childFilterConfig.map((filter) => {
          const isOpen = openKey === filter.key;
          const isActive = filters[filter.key] !== "All";
          const isStars = filter.type === "stars";

          return (
            <div key={filter.key} className="filter-group">
              <label className="filter-label fw-medium">{filter.label}</label>
              <div className="filter-dropdown-wrapper">
                <button
                  className={[
                    "filter-select",
                    isActive ? "filter-select--active" : "",
                    isOpen ? "filter-select--open" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setOpenKey(isOpen ? null : filter.key)}
                >
                  <p
                    className={[
                      "fw-medium",
                      isActive ? "filter-select__text--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {isStars
                      ? getRatingLabel(filters[filter.key])
                      : filters[filter.key]}
                  </p>
                  <svg
                    className={`filter-chevron${
                      isOpen ? " filter-chevron--open" : ""
                    }`}
                    viewBox="0 0 10 6"
                    fill="none"
                  >
                    <path
                      d="M1 1l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {isOpen && (
                  <div className="filter-dropdown">
                    {filter.values.map((opt) => {
                      const isSelected = filters[filter.key] === opt;
                      return (
                        <div
                          key={opt}
                          className={`filter-option${
                            isSelected ? " filter-option--selected" : ""
                          }`}
                          onClick={() => handleSelect(filter.key, opt)}
                        >
                          {isSelected && (
                            <svg
                              className="filter-check-icon"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="#2563eb"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          {isStars && opt !== "All" ? (
                            <StarRow count={opt} />
                          ) : (
                            opt
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openKey && (
        <div className="filter-backdrop" onClick={() => setOpenKey(null)} />
      )}
    </div>
  );
}
